<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import "@jobinsjp/vue3-datatable/dist/style.css";
import {
  DataTable,
  TableBody,
  TableHead,
  TableBodyCell,
} from "@jobinsjp/vue3-datatable";
import moment from "moment";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import {
  appealStatuses,
  appealStatusLabel,
  appealStatusVariant,
} from "@/helpers/constants";

const store = useStore();
const router = useRouter();

const loading = computed(() => store.state.globals.loading);
const tickets = computed(() => store.state.adjudication.tickets);
const pagination = computed(() => store.state.adjudication.pagination);
const summary = computed(() => store.state.adjudication.summary);
const lgus = computed(() => store.state.lgu.dropdownLGUs || []);

const title = ref("Adjudication - Contest");
const items = ref([
  { text: "Home", href: "/" },
  { text: "Adjudication", href: "/adjudication/queue" },
  { text: "Contest", active: true },
]);

const query = ref({ search: "" });

const statusFilter = ref("");
const lguFilter = ref("");
const mineOnly = ref(false);
const includeClosed = ref(false);

// The datatable hands its own state to @loadData; keep the last one so a filter
// change can re-issue the same request from page 1.
const lastLoad = ref({ page: 1, per_page: 10, search: "" });

const getTickets = async (dt) => {
  lastLoad.value = dt;

  await store.dispatch("adjudication/getTickets", {
    page: dt.page,
    pageSize: dt.per_page,
    search: dt.search,
    sortColumn: dt.sort_column || "filed_at",
    orderBy: dt.sort_direction || "desc",
    status: statusFilter.value || undefined,
    lgu_id: lguFilter.value || undefined,
    mine: mineOnly.value ? 1 : undefined,
    include_closed: includeClosed.value ? 1 : undefined,
  });

  await store.dispatch("adjudication/getSummary");
};

const onFilterChange = () => {
  getTickets({ ...lastLoad.value, page: 1 });
};

const formattedDate = (date) =>
  date ? moment(date).format("YYYY-MM-DD h:mm A") : "—";

const formattedDay = (date) => (date ? moment(date).format("YYYY-MM-DD") : "—");

const openTicket = (row) => {
  router.push({ name: "adjudication-view", params: { id: row.id } });
};

const filterByStatus = (status) => {
  statusFilter.value = statusFilter.value === status ? "" : status;
  includeClosed.value = !["", ...openStatuses].includes(statusFilter.value);
  onFilterChange();
};

// Kept in sync with AppealTicket::OPEN_STATUSES on the backend.
const openStatuses = [
  "submitted",
  "under_review",
  "awaiting_documents",
  "hearing_scheduled",
];

onMounted(() => {
  store.dispatch("lgu/getDropdownLGUs");
});
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-overlay :show="loading">
      <div class="row mb-3">
        <div class="col-12">
          <div class="d-flex flex-wrap gap-2">
            <button
              v-for="status in appealStatuses"
              :key="status.value"
              type="button"
              class="btn btn-sm"
              :class="
                statusFilter === status.value
                  ? `btn-${appealStatusVariant(status.value)}`
                  : `btn-outline-${appealStatusVariant(status.value)}`
              "
              @click="filterByStatus(status.value)"
            >
              {{ status.name }}
              <span class="badge bg-light text-dark ms-1">
                {{ summary[status.value] || 0 }}
              </span>
            </button>

            <span class="ms-auto d-flex gap-2">
              <span
                v-if="summary.ack_failed"
                class="badge bg-danger align-self-center"
                title="Acknowledgement email failed — the citizen may never have received their ticket link"
              >
                {{ summary.ack_failed }} ack email failed
              </span>
              <span
                v-if="summary.push_pending"
                class="badge bg-warning align-self-center"
                title="Not yet delivered to the citizen's portal"
              >
                {{ summary.push_pending }} awaiting delivery
              </span>
            </span>
          </div>
        </div>
      </div>

      <div class="d-flex mb-3 justify-content-between align-items-center flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3 flex-wrap">
          <h1 class="fs-4 text mb-0">Contest</h1>

          <select
            class="form-select form-select-sm w-auto"
            v-model="lguFilter"
            @change="onFilterChange"
            title="Filter by LGU"
          >
            <option value="">All LGUs</option>
            <option v-for="lgu in lgus" :key="lgu.id" :value="lgu.id">
              {{ lgu.name }}
            </option>
          </select>

          <div class="form-check mb-0">
            <input
              id="mine-only"
              class="form-check-input"
              type="checkbox"
              v-model="mineOnly"
              @change="onFilterChange"
            />
            <label class="form-check-label" for="mine-only">Assigned to me</label>
          </div>

          <div class="form-check mb-0">
            <input
              id="include-closed"
              class="form-check-input"
              type="checkbox"
              v-model="includeClosed"
              @change="onFilterChange"
            />
            <label class="form-check-label" for="include-closed">Show closed</label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col lg-12">
          <data-table
            :rows="tickets"
            :pagination="pagination"
            striped
            :loading="loading"
            :query="query"
            filter
            hoverable
            sortable
            @loadData="getTickets"
          >
            <template #thead>
              <table-head>Ticket</table-head>
              <table-head>Filed</table-head>
              <table-head>LGU</table-head>
              <table-head>Appellant</table-head>
              <table-head>NOV(s)</table-head>
              <table-head>Status</table-head>
              <table-head>Hearing</table-head>
              <table-head>Assigned</table-head>
              <table-head>Actions</table-head>
            </template>

            <template #tbody="{ row }">
              <table-body>
                <span class="fw-semibold">{{ row.ticket_number }}</span>
                <b-badge
                  v-if="row.unread_from_appellant"
                  variant="danger"
                  class="ms-1"
                  :title="`${row.unread_from_appellant} new message(s) from the appellant`"
                >
                  {{ row.unread_from_appellant }} new
                </b-badge>
                <b-badge
                  v-if="row.ack_email_status === 'failed'"
                  variant="danger"
                  class="ms-1"
                  title="The acknowledgement email failed — the citizen may not have their ticket link"
                >
                  no email
                </b-badge>
                <b-badge
                  v-if="row.push_pending"
                  variant="warning"
                  class="ms-1"
                  title="The latest change has not reached the citizen's portal yet"
                >
                  undelivered
                </b-badge>
              </table-body>

              <table-body>
                {{ formattedDay(row.filed_at) }}
                <div class="text-muted small">
                  {{ row.days_open !== null ? `${row.days_open} day(s)` : "" }}
                </div>
              </table-body>

              <table-body v-text="row.lgu ? row.lgu.name : '—'" />

              <table-body>
                {{ row.appellant_name || "—" }}
                <div class="text-muted small">{{ row.contact_number || "" }}</div>
              </table-body>

              <table-body>
                <div v-for="nov in row.novs" :key="nov.nov_no">
                  {{ nov.nov_no }}
                </div>
              </table-body>

              <table-body>
                <b-badge :variant="appealStatusVariant(row.status)">
                  {{ appealStatusLabel(row.status) }}
                </b-badge>
                <div v-if="row.resolution" class="text-muted small">
                  {{ row.resolution }}
                </div>
              </table-body>

              <table-body v-text="formattedDate(row.hearing_at)" />

              <table-body
                v-text="row.adjudicator ? row.adjudicator.name : 'Unassigned'"
              />

              <table-body>
                <button
                  class="btn btn-primary btn-sm"
                  type="button"
                  title="Open"
                  @click.prevent="openTicket(row)"
                >
                  <i class="ri-eye-line label-icon align-middle"></i>
                </button>
              </table-body>
            </template>

            <template #empty>
              <TableBodyCell colspan="9">No contests found.</TableBodyCell>
            </template>
          </data-table>
        </div>
      </div>
    </b-overlay>
  </Layout>
</template>
